import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { 
  useToggleFavorite, 
  useSyncFavorites,
  favoritesKeys
} from './useFavorites';

/**
 * Hybrid hook that combines Zustand store with React Query for favorites
 * This provides:
 * - Immediate UI updates (via Zustand)
 * - Server synchronization (via React Query)
 * - Offline support (via localStorage)
 * - Optimistic updates
 */
export function useHybridFavorites() {
  const queryClient = useQueryClient();
  const store = useFavoritesStore();
  const toggleFavoriteMutation = useToggleFavorite();
  const syncFavoritesMutation = useSyncFavorites();

  // Sync store with React Query on mount and when coming back online
  useEffect(() => {
    const handleOnline = async () => {
      store.setOnlineStatus(true);
      
      // If we have pending changes, sync them
      if (store.pendingChanges.size > 0) {
        try {
          const result = await syncFavoritesMutation.mutateAsync(store.getFavoriteIds());
          store.updateFromServer(result.serverFavorites.map(f => f.recipeId));
        } catch (error) {
          console.error('Failed to sync favorites:', error);
        }
      }
    };

    const handleOffline = () => {
      store.setOnlineStatus(false);
    };

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial sync if online and no recent sync (less aggressive)
    const now = Date.now();
    const lastSync = store.lastSyncAt;
    const syncInterval = 30 * 60 * 1000; // 30 minutes (increased from 5 minutes)

    // Only sync if we haven't synced in the last 30 minutes AND we have local data
    if (store.isOnline && store.getFavoriteIds().length > 0 && (!lastSync || (now - lastSync) > syncInterval)) {
      // Add a delay to avoid immediate sync on every page load
      const timeoutId = setTimeout(() => {
        syncFavoritesMutation.mutate(store.getFavoriteIds(), {
          onSuccess: (result) => {
            store.updateFromServer(result.serverFavorites.map(f => f.recipeId));
          },
          onError: (error) => {
            console.error('Initial sync failed:', error);
          }
        });
      }, 2000); // 2 second delay

      return () => clearTimeout(timeoutId);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [store, syncFavoritesMutation]);

  // Toggle favorite with hybrid approach
  const toggleFavorite = useCallback(async (recipeId: string) => {
    // Always update local store immediately for instant UI feedback
    store.toggleFavorite(recipeId);

    // If online, also update server
    if (store.isOnline) {
      try {
        const result = await toggleFavoriteMutation.mutateAsync({ recipeId });
        
        // Ensure local state matches server response
        if (result.isFavorite !== store.isFavorite(recipeId)) {
          store.toggleFavorite(recipeId); // Correct mismatch
        }
      } catch (error) {
        // Revert local change if server call failed
        store.toggleFavorite(recipeId);
        console.error('Failed to toggle favorite on server:', error);
        throw error;
      }
    } else {
      // Add to pending changes for later sync
      store.addPendingChange(recipeId);
    }
  }, [store, toggleFavoriteMutation]);

  // Check if recipe is favorite (immediate from store)
  const isFavorite = useCallback((recipeId: string) => {
    return store.isFavorite(recipeId);
  }, [store]);

  // Get all favorite IDs (immediate from store)
  const getFavoriteIds = useCallback(() => {
    return store.getFavoriteIds();
  }, [store]);

  // Clear all favorites
  const clearFavorites = useCallback(async () => {
    // Clear local store immediately
    store.clearFavorites();

    // If online, also clear on server
    if (store.isOnline) {
      try {
        // This would use the clear favorites mutation
        // For now, we'll just invalidate the cache
        queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
      } catch (error) {
        console.error('Failed to clear favorites on server:', error);
      }
    }
  }, [store, queryClient]);

  // Manual sync trigger
  const syncWithServer = useCallback(async () => {
    if (!store.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    try {
      const result = await syncFavoritesMutation.mutateAsync(store.getFavoriteIds());
      store.updateFromServer(result.serverFavorites.map(f => f.recipeId));
      return result;
    } catch (error) {
      console.error('Manual sync failed:', error);
      throw error;
    }
  }, [store, syncFavoritesMutation]);

  // Batch check favorite statuses (uses React Query for efficiency)
  const checkFavoriteStatuses = useCallback((recipeIds: string[]) => {
    // For immediate response, use store data
    const localStatuses = recipeIds.reduce((acc, id) => {
      acc[id] = store.isFavorite(id);
      return acc;
    }, {} as Record<string, boolean>);

    return localStatuses;
  }, [store]);

  return {
    // Core actions
    toggleFavorite,
    isFavorite,
    getFavoriteIds,
    clearFavorites,
    
    // Batch operations
    checkFavoriteStatuses,
    
    // Sync operations
    syncWithServer,
    
    // State
    isOnline: store.isOnline,
    pendingChanges: Array.from(store.pendingChanges),
    lastSyncAt: store.lastSyncAt,
    
    // Loading states
    isToggling: toggleFavoriteMutation.isPending,
    isSyncing: syncFavoritesMutation.isPending,
    
    // Ratings (existing functionality)
    setRating: store.setRating,
    getRating: store.getRating,
  };
}
